/**
 * Armazon de la aplicacion: rutas, cabecera, pie y navegacion.
 *
 * Estructura semantica deliberada: header con la cabecera, nav con la
 * navegacion, main con el contenido y un enlace de salto al contenido como
 * primer elemento enfocable de la pagina.
 *
 * La cabecera tiene tres variantes porque hay tres publicos distintos y antes
 * todos veian la misma. Un administrador no necesita el enlace al mapa ni a la
 * clasificacion, y en el panel esa navegacion solo servia para sacarlo de donde
 * estaba trabajando; quien esta midiendo el GPS en la calle tampoco.
 */

import { Suspense, lazy, useEffect } from 'react';
import { Link, NavLink, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Recorrido } from '@/paginas/Recorrido.tsx';
import { Registro } from '@/paginas/Registro.tsx';
import { Clasificacion } from '@/paginas/Clasificacion.tsx';
import { Privacidad } from '@/paginas/politica/Privacidad.tsx';
import { SelectorIdioma } from '@/componentes/SelectorIdioma.tsx';
import { Logotipo } from '@/componentes/Logotipo.tsx';
import { useAnuncios } from '@/componentes/Anuncio.tsx';
import { useCerrarSesion, useSesion } from '@/api/consultas.ts';

/**
 * El panel de administracion se carga aparte: es un porcentaje grande del
 * codigo y no lo usa nadie durante el recorrido, que es cuando la conexion
 * movil importa.
 */
const Administracion = lazy(async () => {
  const modulo = await import('@/paginas/admin/Administracion.tsx');
  return { default: modulo.Administracion };
});

/**
 * El diagnostico de GPS tampoco viaja con la aplicacion: se usa en las salidas
 * de campo, no durante un evento.
 */
const Diagnostico = lazy(async () => {
  const modulo = await import('@/paginas/Diagnostico.tsx');
  return { default: modulo.Diagnostico };
});

export function App() {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const variante = pathname.startsWith('/admin')
    ? 'admin'
    : pathname.startsWith('/diagnostic')
      ? 'minima'
      : 'publica';

  return (
    <div className="flex min-h-dvh flex-col bg-crema-50">
      <a href="#contenido" className="salto-contenido">
        {t('app.saltarAlContingut')}
      </a>

      <Cabecera variante={variante} />

      {/*
        `min-h-0` ademas de `flex-1`: sin eso un hijo que quiera repartirse la
        altura (el mapa del recorrido) no puede encogerse por debajo de su
        contenido y desborda la pantalla en lugar de ajustarse.
      */}
      <main id="contenido" tabIndex={-1} className="flex min-h-0 flex-1 flex-col">
        <Routes>
          <Route path="/" element={<Recorrido />} />
          <Route path="/registro/:codigo" element={<Registro />} />
          <Route path="/classificacio" element={<Clasificacion />} />
          <Route path="/privacitat" element={<Privacidad />} />
          {/*
            Sin enlace desde la navegacion: es una herramienta de trabajo, no
            una pantalla del recorrido. Se llega escribiendo la direccion.
          */}
          <Route
            path="/diagnostic"
            element={
              <Suspense fallback={<Cargando />}>
                <Diagnostico />
              </Suspense>
            }
          />
          <Route
            path="/admin/*"
            element={
              <Suspense fallback={<Cargando />}>
                <Administracion />
              </Suspense>
            }
          />
          {/*
            Un enlace de acceso solo llega hasta aqui si esta mal formado: los
            correctos los intercepta el servidor antes que el enrutador. Pasa
            cuando el enlace del correo se corta al copiarlo o al reenviarlo por
            mensajeria, y conviene decirlo con esas palabras en vez de soltar un
            "no encontrado" que no ayuda a nadie.
          */}
          <Route path="/s/*" element={<EnlaceDeAccesoNoValido />} />
          <Route path="*" element={<NoEncontrado />} />
        </Routes>
      </main>

      <Pie />
    </div>
  );
}

/** Espera de carga con la forma de la aplicacion, no un parrafo suelto. */
function Cargando() {
  const { t } = useTranslation();

  return (
    <div className="contenedor py-16">
      <p role="status" className="text-center text-tinta-500">
        {t('app.carregant')}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Paginas de error de ruta                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Dirección que no corresponde a ninguna pantalla.
 *
 * Antes esto mostraba "no hemos encontrado este evento", que mandaba a buscar
 * el fallo en los datos cuando lo que fallaba era la dirección. Dice qué ha
 * pasado y ofrece la salida, que es volver al recorrido.
 */
function NoEncontrado() {
  const { t } = useTranslation();

  return <PaginaDeAviso titulo={t('errors.rutaTitol')} detalle={t('errors.rutaDetall')} />;
}

/**
 * Enlace de acceso incompleto o mal copiado.
 *
 * Se distingue del anterior porque la salida es distinta: aquí no sirve de nada
 * ir al recorrido sin sesión, lo que hace falta es volver al correo y usar el
 * enlace entero, o pedir que se lo reenvíen desde la organización.
 */
function EnlaceDeAccesoNoValido() {
  const { t } = useTranslation();

  return <PaginaDeAviso titulo={t('errors.enllacTitol')} detalle={t('errors.enllacDetall')} />;
}

function PaginaDeAviso({ titulo, detalle }: { titulo: string; detalle: string }) {
  const { t } = useTranslation();

  return (
    <div className="contenedor-estrecho py-12 sm:py-20">
      <div className="tarjeta text-center">
        <h1 className="titulo-seccion">{titulo}</h1>
        <p className="mt-3 text-tinta-700">{detalle}</p>
        <Link to="/" className="boton-primario mt-6">
          {t('errors.anarAlRecorregut')}
        </Link>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Pie                                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Pie con el enlace permanente a la política de privacidad.
 *
 * Va aquí y no solo en el formulario porque quien ya se inscribió tiene derecho
 * a consultar en cualquier momento qué se hace con sus datos y cómo retirar el
 * consentimiento, sin tener que volver a un formulario que ya no verá.
 */
function Pie() {
  const { t } = useTranslation();

  return (
    <footer className="mt-auto border-t border-crema-200 bg-white">
      <div
        className="contenedor-ancho flex flex-col gap-3 py-6 sm:flex-row sm:items-center
                   sm:justify-between"
      >
        <p className="flex items-center gap-2 text-sm text-tinta-500">
          <span className="text-tinta-700">
            <Logotipo tamano={22} soloSigno />
          </span>
          {t('peu.credit')}
        </p>
        <Link
          to="/privacitat"
          className="text-sm text-tinta-700 underline underline-offset-4 hover:text-roig-700"
        >
          {t('peu.privacitat')}
        </Link>
      </div>
    </footer>
  );
}

/* -------------------------------------------------------------------------- */
/* Cabecera                                                                    */
/* -------------------------------------------------------------------------- */

function Cabecera({ variante }: { variante: 'publica' | 'admin' | 'minima' }) {
  const { t } = useTranslation();
  const consultaSesion = useSesion();
  const cerrarSesion = useCerrarSesion();
  const { anunciar } = useAnuncios();
  const [parametros, setParametros] = useSearchParams();

  /**
   * El servidor redirige aqui tras canjear el QR, con una marca en la consulta.
   * Se anuncia el resultado y se limpia la URL, para no dejar el aviso pegado
   * si la persona recarga.
   */
  useEffect(() => {
    const sesion = parametros.get('sesion');
    if (!sesion) return;

    anunciar(sesion === 'iniciada' ? t('sessio.iniciada') : t('sessio.noValida'));
    parametros.delete('sesion');
    setParametros(parametros, { replace: true });
  }, [parametros, setParametros, anunciar, t]);

  const equipo = consultaSesion.data;

  return (
    <header className="border-b border-crema-200 bg-white">
      {/*
        Dos filas SIEMPRE, y no una fila que se parte cuando no cabe. Antes la
        navegacion compartia fila con el nombre y el selector de idioma con un
        `flex-wrap`, y en movil el resultado era "Mapa" colgando sobre
        "Clasificacion" con el nombre al lado, descolocado. Separar las filas
        cuesta 40 pixeles de alto y quita el problema de raiz.
      */}
      <div className="contenedor-ancho flex items-center justify-between gap-3 py-3">
        <Link
          to={variante === 'admin' ? '/admin' : '/'}
          className="flex items-center rounded-lg text-tinta-900 transition-colors
                     hover:text-roig-700"
        >
          <Logotipo tamano={30} />
          <span className="sr-only">{t('app.nom')}</span>
        </Link>

        <div className="flex items-center gap-2 sm:gap-3">
          <SelectorIdioma />

          {variante === 'publica' && equipo ? (
            <div className="flex items-center gap-2">
              <span className="hidden max-w-[14ch] truncate text-sm text-tinta-700 sm:inline">
                {equipo.nombre}
              </span>
              {/*
                En movil solo el icono. "Tancar la sessio" son tres palabras que
                en una pantalla de 390 px se partian en tres lineas y desmontaban
                la cabecera entera. El texto sigue ahi para lectores de pantalla
                y aparece en cuanto hay sitio.
              */}
              <button
                type="button"
                className="boton-sutil"
                onClick={() => {
                  cerrarSesion.mutate();
                }}
              >
                <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" className="size-5 sm:hidden">
                  <path
                    d="M12.5 6.5V4.75A1.25 1.25 0 0 0 11.25 3.5h-5A1.25 1.25 0 0 0 5 4.75v10.5a1.25 1.25 0 0 0 1.25 1.25h5a1.25 1.25 0 0 0 1.25-1.25V13.5M9 10h8m0 0-2.5-2.5M17 10l-2.5 2.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="sr-only sm:not-sr-only">{t('sessio.sortir')}</span>
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {variante === 'publica' ? (
        <div className="contenedor-ancho">
          <nav aria-label={t('navegacio.menu')} className="pestanas border-b-0">
            <EnlaceNavegacion a="/" fin>
              {t('navegacio.mapa')}
            </EnlaceNavegacion>
            <EnlaceNavegacion a="/classificacio">{t('navegacio.classificacio')}</EnlaceNavegacion>
          </nav>
        </div>
      ) : null}

      {variante === 'admin' ? (
        <div className="contenedor-ancho">
          <nav aria-label={t('navegacio.menu')} className="pestanas border-b-0">
            {/*
              Un enlace de vuelta al sitio publico y nada mas: desde el panel se
              quiere ir a ver como ha quedado, no navegar por el recorrido.
            */}
            <EnlaceNavegacion a="/" fin>
              {t('admin.veureElLloc')}
            </EnlaceNavegacion>
          </nav>
        </div>
      ) : null}
    </header>
  );
}

function EnlaceNavegacion({
  a,
  fin = false,
  children,
}: {
  a: string;
  fin?: boolean;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={a}
      end={fin}
      className={({ isActive }) => `pestana ${isActive ? 'pestana-activa' : ''}`}
    >
      {children}
    </NavLink>
  );
}
